import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Smartphone, X } from 'lucide-react';
import toast from 'react-hot-toast';
import type {
  DeepLinkCampaign,
  DeepLinkCampaignFormValues,
  DeepLinkPlatform,
  DeepLinkRoute,
  DeepLinkScreenCatalog,
  DeepLinkScreenCatalogItem,
} from '../../../models/MasterAdmin/deepLink';
import {
  DEEP_LINK_PLATFORM_OPTIONS,
  DEEP_LINK_STATUS_OPTIONS,
  DEEP_LINK_TARGET_APP_OPTIONS,
  describeAppDeepLinkDestination,
} from '../../../models/MasterAdmin/deepLink';
import {
  ensureDeepLinkRouteFromScreen,
  fetchDeepLinkScreenCatalog,
  previewDeepLink,
} from '../../../services/MasterAdmin/deepLinkService';
import {
  dlBtnPrimaryClass,
  dlBtnSecondaryClass,
  dlInfoBoxClass,
  dlInputClass,
  dlLabelClass,
  dlModalHeaderClass,
  dlModalOverlayClass,
  dlModalPanelWideClass,
  dlPlatformChipActive,
  dlPlatformChipInactive,
  dlSectionBoxClass,
  dlSelectClass,
  dlSubLabelClass,
  dlTextareaClass,
} from './deepLinkUi';
import { ScreenSeedHint } from './RouteSeedStatusBadge';
import type { DeepLinkTargetAppKey } from '../../../models/MasterAdmin/deepLink';

type Props = {
  open: boolean;
  mode: 'create' | 'edit';
  routes: DeepLinkRoute[];
  initial?: DeepLinkCampaign | null;
  onClose: () => void;
  onSubmit: (values: DeepLinkCampaignFormValues) => Promise<void>;
  onRoutesChanged?: () => void;
};

const DeepLinkCampaignFormModal: React.FC<Props> = ({
  open,
  mode,
  routes,
  initial,
  onClose,
  onSubmit,
  onRoutesChanged,
}) => {
  const [saving, setSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalog, setCatalog] = useState<DeepLinkScreenCatalog | null>(null);
  const [selectedService, setSelectedService] = useState('');
  const [selectedScreenName, setSelectedScreenName] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [form, setForm] = useState<DeepLinkCampaignFormValues>({
    name: '',
    description: '',
    routeId: '',
    routeParams: {},
    targetPlatforms: ['ANDROID', 'IOS', 'WEB', 'APP'],
    targetAppKey: 'user',
    loginRequired: true,
    status: 'DRAFT',
    startsAt: '',
    expiresAt: '',
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    utmContent: '',
    utmTerm: '',
    minAppVersion: '',
    maxAppVersion: '',
  });

  const screensForService = useMemo(() => {
    if (!catalog || !selectedService) return [];
    return catalog.screensByService[selectedService] || [];
  }, [catalog, selectedService]);

  const selectedScreen = useMemo(
    () => screensForService.find((s) => s.screenName === selectedScreenName) || null,
    [screensForService, selectedScreenName]
  );

  const selectedRoute = useMemo(() => {
    if (selectedScreen?.routeId) {
      return routes.find((r) => r.id === selectedScreen.routeId) || null;
    }
    return routes.find((r) => r.id === form.routeId) || null;
  }, [routes, form.routeId, selectedScreen]);

  useEffect(() => {
    if (!open) return;
    setCatalogLoading(true);
    const appKey = (form.targetAppKey || 'user') as DeepLinkTargetAppKey;
    void fetchDeepLinkScreenCatalog(appKey)
      .then(setCatalog)
      .catch(() => toast.error('Failed to load app screen catalog'))
      .finally(() => setCatalogLoading(false));
  }, [open, form.targetAppKey]);

  useEffect(() => {
    if (!open) {
      setInitialized(false);
      return;
    }

    if (initialized) return;

    // If editing, we must wait for both catalog and routes to load.
    if (initial && (!catalog || !routes.length)) {
      return;
    }
    // If creating, we must wait for catalog to load.
    if (!initial && !catalog) {
      return;
    }

    const resetFromRoute = (routeId: string, loginRequired: boolean, routeParams = {}) => {
      const route = routes.find((r) => r.id === routeId);
      let service = '';
      let screenName = '';

      if (route?.screenName && catalog) {
        screenName = route.screenName;
        service = route.screenName.split(':')[2] || '';
      } else if (route && catalog) {
        for (const [svc, screens] of Object.entries(catalog.screensByService)) {
          const match = screens.find(
            (s) => s.routeId === route.id || s.slug === route.slug || s.appRoute === route.appRoute
          );
          if (match) {
            service = svc;
            screenName = match.screenName;
            break;
          }
        }
      }

      if (!service && catalog?.services.length) {
        service = catalog.services[0].key;
      }

      setSelectedService(service);
      setSelectedScreenName(screenName);
      setForm((prev) => ({
        ...prev,
        routeId,
        loginRequired,
        routeParams,
      }));
    };

    if (initial) {
      setForm({
        name: initial.name,
        description: initial.description || '',
        routeId: initial.routeId,
        routeParams: initial.routeParams || {},
        targetPlatforms: initial.targetPlatforms || ['ANDROID', 'IOS', 'WEB', 'APP'],
        targetAppKey: initial.targetAppKey || 'user',
        loginRequired: initial.loginRequired,
        status: initial.status,
        startsAt: initial.startsAt ? initial.startsAt.slice(0, 16) : '',
        expiresAt: initial.expiresAt ? initial.expiresAt.slice(0, 16) : '',
        utmSource: initial.utmSource || '',
        utmMedium: initial.utmMedium || '',
        utmCampaign: initial.utmCampaign || '',
        utmContent: initial.utmContent || '',
        utmTerm: initial.utmTerm || '',
        minAppVersion: initial.minAppVersion || '',
        maxAppVersion: initial.maxAppVersion || '',
      });
      resetFromRoute(initial.routeId, initial.loginRequired, initial.routeParams || {});
    } else if (catalog) {
      const firstService = catalog.services[0]?.key || '';
      const firstScreen = catalog.screensByService[firstService]?.[0];
      setSelectedService(firstService);
      setSelectedScreenName(firstScreen?.screenName || '');
      setForm((prev) => ({
        name: prev.name || '',
        description: prev.description || '',
        routeId: firstScreen?.routeId || '',
        routeParams: {},
        targetPlatforms: prev.targetPlatforms?.length
          ? prev.targetPlatforms
          : ['ANDROID', 'IOS', 'WEB', 'APP'],
        targetAppKey: catalog.targetAppKey || prev.targetAppKey || 'user',
        loginRequired: firstScreen?.requiresAuth ?? true,
        status: prev.status || 'DRAFT',
        startsAt: prev.startsAt || '',
        expiresAt: prev.expiresAt || '',
        utmSource: prev.utmSource || '',
        utmMedium: prev.utmMedium || '',
        utmCampaign: prev.utmCampaign || '',
        utmContent: prev.utmContent || '',
        utmTerm: prev.utmTerm || '',
        minAppVersion: prev.minAppVersion || '',
        maxAppVersion: prev.maxAppVersion || '',
      }));
    }
    setPreviewUrl(null);
    setInitialized(true);
  }, [open, initial, catalog, routes, initialized, form.targetAppKey]);

  const handleTargetAppChange = (targetAppKey: DeepLinkTargetAppKey) => {
    setForm((p) => ({ ...p, targetAppKey, routeId: '' }));
    setSelectedService('');
    setSelectedScreenName('');
    setPreviewUrl(null);
  };

  const handleServiceChange = (serviceKey: string) => {
    setSelectedService(serviceKey);
    const first = catalog?.screensByService[serviceKey]?.[0];
    setSelectedScreenName(first?.screenName || '');
    setForm((p) => ({
      ...p,
      routeId: first?.routeId || '',
      routeParams: {},
      loginRequired: first?.requiresAuth ?? true,
    }));
  };

  const handleScreenChange = async (screenName: string) => {
    setSelectedScreenName(screenName);
    const screen = screensForService.find((s) => s.screenName === screenName);
    if (!screen) return;

    let routeId = screen.routeId;
    if (!routeId) {
      try {
        const route = await ensureDeepLinkRouteFromScreen(screenName);
        routeId = route.id;
        onRoutesChanged?.();
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Could not register screen route');
        return;
      }
    }

    setForm((p) => ({
      ...p,
      routeId: routeId || '',
      routeParams: {},
      loginRequired: screen.requiresAuth,
    }));
  };

  const togglePlatform = (platform: DeepLinkPlatform) => {
    setForm((prev) => {
      const set = new Set(prev.targetPlatforms);
      if (set.has(platform)) set.delete(platform);
      else set.add(platform);
      return { ...prev, targetPlatforms: Array.from(set) };
    });
  };

  const handleParamChange = (name: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      routeParams: { ...(prev.routeParams || {}), [name]: value },
    }));
  };

  const handlePreview = async () => {
    if (!form.routeId) return;
    const result = await previewDeepLink({
      routeId: form.routeId,
      routeParams: form.routeParams,
      loginRequired: form.loginRequired,
      utmSource: form.utmSource,
      utmMedium: form.utmMedium,
      utmCampaign: form.utmCampaign || form.name,
      utmContent: form.utmContent,
      utmTerm: form.utmTerm,
    });
    setPreviewUrl(result.fullUrl);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.routeId && selectedScreenName) {
      await handleScreenChange(selectedScreenName);
    }
    if (!form.routeId) {
      toast.error('Select a target screen');
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        ...form,
        startsAt: form.startsAt || undefined,
        expiresAt: form.expiresAt || undefined,
        utmSource: form.utmSource || undefined,
        utmMedium: form.utmMedium || undefined,
        utmCampaign: form.utmCampaign || form.name,
        utmContent: form.utmContent || undefined,
        utmTerm: form.utmTerm || undefined,
        minAppVersion: form.minAppVersion || undefined,
        maxAppVersion: form.maxAppVersion || undefined,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const paramSchema =
    selectedScreen?.paramSchema?.length
      ? selectedScreen.paramSchema
      : selectedRoute?.paramSchema || [];

  if (!open) return null;

  return (
    <div className={dlModalOverlayClass}>
      <div className={dlModalPanelWideClass}>
        <div className={dlModalHeaderClass}>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {mode === 'create' ? 'Create deep link campaign' : 'Edit campaign'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="lg:grid lg:grid-cols-2 lg:divide-x lg:divide-gray-200 dark:lg:divide-gray-700">
            {/* Left column: basics + destination */}
            <div className="space-y-5 p-4 sm:p-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <label className="block text-sm">
              <span className={dlLabelClass}>Campaign name</span>
              <input
                required
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className={dlInputClass}
              />
            </label>
            <label className="block text-sm">
              <span className={dlLabelClass}>Status</span>
              <select
                value={form.status}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    status: e.target.value as DeepLinkCampaignFormValues['status'],
                  }))
                }
                className={dlSelectClass}
              >
                {DEEP_LINK_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block text-sm">
            <span className={dlLabelClass}>Description</span>
            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              className={dlTextareaClass}
              rows={2}
            />
          </label>

          <div className={`${dlSectionBoxClass} space-y-4`}>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
              <Smartphone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              App destination
            </div>

            <div>
              <p className={dlLabelClass}>Target audience</p>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                Choose User app or Vendor (Plus) app — available screens update automatically.
              </p>
              <div className="flex flex-wrap gap-2">
                {DEEP_LINK_TARGET_APP_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleTargetAppChange(option.value)}
                    className={
                      (form.targetAppKey || 'user') === option.value
                        ? dlPlatformChipActive
                        : dlPlatformChipInactive
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {catalog?.summary ? (
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                  {catalog.summary.totalScreens} screens
                </span>
                <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                  {catalog.summary.seededCount} seeded
                </span>
                <span className="rounded-full bg-amber-100 px-2 py-1 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                  {catalog.summary.pendingCount} pending seed
                </span>
              </div>
            ) : null}

            {catalogLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading screens from app registry…
              </div>
            ) : (
              <>
                <label className="block text-sm">
                  <span className={dlLabelClass}>Service</span>
                  <select
                    value={selectedService}
                    onChange={(e) => handleServiceChange(e.target.value)}
                    className={dlSelectClass}
                  >
                    <option value="">Select service</option>
                    {catalog?.services.map((svc) => (
                      <option key={svc.key} value={svc.key}>
                        {svc.label} ({svc.screenCount} screens)
                      </option>
                    ))}
                  </select>
                </label>

                <label className="block text-sm">
                  <span className={dlLabelClass}>Target screen</span>
                  <select
                    required
                    value={selectedScreenName}
                    onChange={(e) => void handleScreenChange(e.target.value)}
                    className={dlSelectClass}
                    disabled={!selectedService}
                  >
                    <option value="">Select screen</option>
                    {screensForService.map((screen: DeepLinkScreenCatalogItem) => (
                      <option key={screen.screenName} value={screen.screenName}>
                        {screen.label} {screen.hasDbRoute ? '✓' : '· pending seed'}
                      </option>
                    ))}
                  </select>
                  <ScreenSeedHint screen={selectedScreen} />
                  {selectedScreen ? (
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Redirect is chosen by <strong>Platform targeting</strong> (Web / Android / iOS):
                      {selectedScreen.webPath ? (
                        <>
                          {' '}
                          web → <code className="text-gray-700 dark:text-gray-300">{selectedScreen.webPath}</code>;
                        </>
                      ) : (
                        <> web → mapped web page;</>
                      )}
                      {' '}
                      app → <code className="text-gray-700 dark:text-gray-300">{describeAppDeepLinkDestination(selectedScreen)}</code>
                      {selectedScreen.slug ? (
                        <>
                          {' '}
                          · slug <code className="text-gray-700 dark:text-gray-300">{selectedScreen.slug}</code>
                        </>
                      ) : null}
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Pick a screen for this service. Use platform chips on the right to target Web, Android, or iOS.
                    </p>
                  )}
                </label>
              </>
            )}
          </div>

          {paramSchema.length ? (
            <div className={dlSectionBoxClass}>
              <p className={`mb-3 text-sm font-medium text-gray-700 dark:text-gray-200`}>
                Route parameters
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                {paramSchema.map((param) => (
                  <label key={param.name} className="block text-sm">
                    <span className={dlSubLabelClass}>
                      {param.label || param.name}
                      {param.required ? ' *' : ''}
                    </span>
                    <input
                      required={!!param.required}
                      value={form.routeParams?.[param.name] || ''}
                      onChange={(e) => handleParamChange(param.name, e.target.value)}
                      className={dlInputClass}
                    />
                  </label>
                ))}
              </div>
            </div>
          ) : null}
            </div>

            {/* Right column: targeting + schedule + UTM */}
            <div className="space-y-5 border-t border-gray-200 p-4 sm:p-6 lg:border-t-0">
          <div>
            <p className={dlLabelClass}>Store listing (when app not installed)</p>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              Uses the target audience above —{' '}
              {(form.targetAppKey || 'user') === 'plus'
                ? 'Vedika.Health Plus (service provider)'
                : 'Vedika.Health (user app)'}
              .
            </p>
          </div>

          <div>
            <p className={dlLabelClass}>Platform targeting</p>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              Same screen — different destination per platform: Web opens the website; Android/iOS open the app
              (Play Store / App Store if not installed).
            </p>
            <div className="flex flex-wrap gap-2">
              {DEEP_LINK_PLATFORM_OPTIONS.map((p) => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => togglePlatform(p.value)}
                  className={
                    form.targetPlatforms.includes(p.value)
                      ? dlPlatformChipActive
                      : dlPlatformChipInactive
                  }
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input
              type="checkbox"
              checked={form.loginRequired}
              onChange={(e) => setForm((p) => ({ ...p, loginRequired: e.target.checked }))}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            <span>Login required before opening target screen</span>
          </label>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm">
              <span className={dlLabelClass}>Starts at</span>
              <input
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => setForm((p) => ({ ...p, startsAt: e.target.value }))}
                className={dlInputClass}
              />
            </label>
            <label className="block text-sm">
              <span className={dlLabelClass}>Expires at</span>
              <input
                type="datetime-local"
                value={form.expiresAt}
                onChange={(e) => setForm((p) => ({ ...p, expiresAt: e.target.value }))}
                className={dlInputClass}
              />
            </label>
          </div>

          <div className={`${dlSectionBoxClass} space-y-3`}>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              UTM / campaign tracking
            </p>
            <div className="grid gap-3 md:grid-cols-2">
              {(['utmSource', 'utmMedium', 'utmCampaign', 'utmContent', 'utmTerm'] as const).map(
                (key) => (
                  <label key={key} className="block text-sm">
                    <span className={dlSubLabelClass}>{key}</span>
                    <input
                      value={form[key] || ''}
                      onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                      className={dlInputClass}
                    />
                  </label>
                )
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="block text-sm">
              <span className={dlLabelClass}>Min app version</span>
              <input
                placeholder="e.g. 2.1.0"
                value={form.minAppVersion}
                onChange={(e) => setForm((p) => ({ ...p, minAppVersion: e.target.value }))}
                className={dlInputClass}
              />
            </label>
            <label className="block text-sm">
              <span className={dlLabelClass}>Max app version</span>
              <input
                placeholder="e.g. 3.0.0"
                value={form.maxAppVersion}
                onChange={(e) => setForm((p) => ({ ...p, maxAppVersion: e.target.value }))}
                className={dlInputClass}
              />
            </label>
          </div>

          {previewUrl ? <div className={dlInfoBoxClass}>Preview: {previewUrl}</div> : null}
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 px-4 py-4 dark:border-gray-700 sm:px-6">
            <button type="button" onClick={() => void handlePreview()} className={dlBtnSecondaryClass}>
              Preview URL
            </button>
            <button type="button" onClick={onClose} className={dlBtnSecondaryClass}>
              Cancel
            </button>
            <button type="submit" disabled={saving} className={dlBtnPrimaryClass}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {mode === 'create' ? 'Create campaign' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeepLinkCampaignFormModal;
